import React, { useCallback, useMemo, useState } from 'react';

import { ExportActionsTypes, FileTypeSelectionCommonForm, Modal, Table } from '@components/common';
import Confirm from '@components/common/Confirm';
import DescriptionModal from '@components/common/ModalComponents/DescriptionModal/DescriptionModal';
import RequestActionConfirmationModal
	from '@components/routes/AdminPanel/AssetsRent/RequestReservationCard/RequestActionConfirmationModal';
import AddNewKpiAlert from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule';

import { useDeleteKpiAlerts } from '@hooks/kpiAlerts/useDeleteKpiAlerts';
import { useExportKpiAlerts } from '@hooks/kpiAlerts/useExportKpiAlerts';
import { useKPIAlerts } from '@hooks/kpiAlerts/useKPIAlerts';

import { kpiAlerts as kpiAlertsSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSnackbar } from '@slices/common';
import {
	setPage,
	setPageSize,
	setSearchValue,
	setSelectedSeverity,
	setSort,
	setStatus,
} from '@slices/kpiAlerts';

import { isEmpty } from '@utils/common';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { IHandlePreparingExportParams } from '@components/common/FileTypeSelectionForm/FileTypeSelectionForm.types';

import { ESnackbarTypes, TVendorsValues } from '@enums/common';
import { EKpiAlertsStatus, TSeverity } from '@enums/kpi-common';

import { exportRadioButtonsData } from '@constants/common';
import { modalCloseConfirm } from '@constants/modals';
import { SeverityOptions, StatusOptions } from '../constants';

import { getColumns } from '../utils';

import EditKpiAlert from '../EditKpiAlertRule';

import styles from './KpiAlertRulesTable.module.scss';

const MODAL_STYLES = {
	MAX_HEIGHT: '80vh',
	WIDTH: '815px',
	HEIGHT: 'max-content',
} as const;

interface IKpiAlertsTableProps {
	selectedVendor: TSelectOption<TVendorsValues>;
}

function KpiAlertRulesTable({
	selectedVendor,
}: IKpiAlertsTableProps) {
	const dispatch = useAppDispatch();

	const [notes, setNotes] = useState<string | null>(null);
	const [deletingIds, setDeletingIds] = useState<string[]>([]);
	const [editableAlertId, setEditableAlertId] = useState<string | null>(null);
	const [notesId, setNotesId] = useState<string | null>(null);
	const [exportingConfig, setExportingConfig] = useState<{ ids: string[]; actionType: string } | null>(null);

	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [isCloseEditConfirmModalShown, setIsCloseEditConfirmModalShown] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isDirtyForm, setIsDirtyForm] = useState(false);
	const [isExportModalOpen, setIsExportModalOpen] = useState(false);

	const closeEditConfirmModal = useCallback(() => setIsCloseEditConfirmModalShown(false), []);

	const openExportModal = useCallback((ids: string[], actionType: string) => {
		setExportingConfig({ ids, actionType });

		setIsExportModalOpen(true);
	}, []);

	const closeExportModal = useCallback(() => {
		setIsExportModalOpen(false);
	}, []);

	const {
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		selectedSeverity,
		status,
	} = useAppSelector(kpiAlertsSelector);

	const {
		kpiAlerts,
		isLoading,
		totalElements,
	} = useKPIAlerts({
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		severity: selectedSeverity?.value || null,
		isActive: status?.value === EKpiAlertsStatus.active
			? true
			: status?.value === EKpiAlertsStatus.inactive
				? false
				: null,
		vendor: selectedVendor?.value || null,
	});

	const {
		isLoading: isExporting,
		exportKpiAlerts,
	} = useExportKpiAlerts({
		vendor: selectedVendor?.value,
		onSuccess: () => setExportingConfig(null),
		filter: {
			searchValue,
			severity: selectedSeverity?.value as TSeverity || null,
			isActive: status?.value === EKpiAlertsStatus.active
				? true
				: status?.value === EKpiAlertsStatus.inactive
					? false
					: undefined,
		},
		ids: exportingConfig?.ids,
		isAll: exportingConfig
			? exportingConfig.actionType === ExportActionsTypes.all
			|| exportingConfig.actionType === ExportActionsTypes.allFiltered
			: false,
	});

	const {
		deleteKpiAlerts,
		isLoading: isDeleting,
	} = useDeleteKpiAlerts({
		onSuccess: () => {
			setDeletingIds([]);
			closeDeleteModal();

			dispatch(setSnackbar({
				type: ESnackbarTypes.success,
				message: 'The KPI alerts rule(s) has been deleted!',
			}));
		},
		onError: () => {
			setDeletingIds([]);
			closeDeleteModal();

			dispatch(setSnackbar({
				type: ESnackbarTypes.error,
				message: 'Something went wrong. Please try again later',
			}));
		},
		vendor: selectedVendor?.value,
	});

	const openNotesModal = (value: string, id: string) => {
		setNotesId(id);
		setNotes(value);
	};

	const openEditForm = (id: string) => setEditableAlertId(id);

	const openCloseEditConfirmModal = useCallback(() => setIsCloseEditConfirmModalShown(true), []);

	const closeEditForm = useCallback(() => {
		setEditableAlertId(null);
		closeEditConfirmModal();
		setIsDirtyForm(false);
	}, [closeEditConfirmModal]);

	const openDeleteModal = useCallback(() => setIsDeleteModalOpen(true), []);

	const onDelete = useCallback((ids: string[]) => {
		openDeleteModal();
		setDeletingIds(ids);
	}, [openDeleteModal]);

	const columns = useMemo(() => {
		return getColumns({
			openEditForm,
			openNotesModal,
			onDelete,
			notesId,
		});
	}, [notesId, onDelete]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const onSearchHandler = useCallback(
		(searchValue: string) => dispatch(setSearchValue({ searchValue })),
		[dispatch],
	);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback(
		(pageSize: number) => dispatch(setPageSize({ pageSize })),
		[dispatch],
	);

	const pagination: ITablePagination = useMemo(() => {
		return {
			countOfRecords: totalElements,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		};
	}, [page, pageHandler, pageSize, pageSizeHandler, totalElements]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			placeholder: 'Search text',
		},
	], [onSearchHandler, searchValue]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	const handleSetSelectedSeverity = useCallback((severity: TSelectOption<string>) => {
		dispatch(setSelectedSeverity({ selectedSeverity: severity }));
	}, [dispatch]);

	const handleSetStatus = useCallback((status: TSelectOption<string>) => {
		dispatch(setStatus({ status: status || null }));
	}, [dispatch]);

	const handleToggleAddNotification = () => {
		setIsAddModalOpen((prevState) => !prevState);
	};

	const onCloseAddModal = useCallback(() => {
		setIsAddModalOpen(false);
		setIsDirtyForm(false);
	}, []);

	const onCloseDescriptionModal = useCallback(() => {
		setNotes(null);
		setNotesId(null);
	}, []);

	const handleDeleteNotification = useCallback(() => {
		if (!isEmpty(deletingIds)) {
			deleteKpiAlerts(deletingIds!);
		}

		setDeletingIds([]);
		closeDeleteModal();
	}, [deleteKpiAlerts, deletingIds]);

	const closeDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setDeletingIds([]);
	};

	const filters: TTableFilter[] = useMemo(() => {
		return [
			{
				name: 'Severity',
				placeholder: 'Severity',
				option: selectedSeverity,
				options: SeverityOptions,
				minWidth: '200px',
				setState: handleSetSelectedSeverity,
				defaultValue: null,
			},
			{
				name: 'Status',
				placeholder: 'Status',
				option: status,
				options: StatusOptions,
				minWidth: '200px',
				setState: handleSetStatus,
				defaultValue: null,
			},
		];
	}, [handleSetSelectedSeverity, handleSetStatus, selectedSeverity, status]);

	const handleExportKpiAlerts = useCallback(({
		fileType,
	}: IHandlePreparingExportParams): void => {
		exportKpiAlerts(fileType);
	}, [exportKpiAlerts]);

	const handleCloseEditModal = () => {
		if (isDirtyForm) {
			openCloseEditConfirmModal();
		} else {
			closeEditForm();
		}
	};

	return (
		<>
			<div className={styles.kpiNotificationsTable}>
				<Table
					addTooltipText='Add new KPI alert rule'
					columns={columns}
					data={kpiAlerts}
					exportTooltipText='Export KPI alert rules'
					filters={filters}
					handleExport={openExportModal}
					isCreatable
					isExport
					isExportSettings={false}
					isLoading={isLoading || isDeleting || isExporting}
					isOpenSearch={!!searchValue?.length}
					isWithoutDeleteActionMenu
					pagination={pagination}
					search={search}
					searchThreshold={0}
					setCheckedIds={setDeletingIds}
					sort={sort}
					toggleAddModal={handleToggleAddNotification}
					toggleDeleteModal={openDeleteModal}
				/>
				<Modal
					height={MODAL_STYLES.HEIGHT}
					isConfirmDialogNeededOnClose
					isDirtyForm={isDirtyForm}
					isOpen={isAddModalOpen}
					isScrollableContent
					maxHeight={MODAL_STYLES.MAX_HEIGHT}
					onClose={onCloseAddModal}
					title='Add alert rule'
					width={MODAL_STYLES.WIDTH}
				>
					<AddNewKpiAlert
						closeModal={onCloseAddModal}
						negativeBtnText='Back'
						positiveBtnText='Next'
						setIsDirtyForm={setIsDirtyForm}
					/>
				</Modal>
				{notes && (
					<Modal
						isConfirmDialogNeededOnClose
						isOpen={!!notes}
						onClose={onCloseDescriptionModal}
						title='Description'
					>
						<DescriptionModal
							description={notes}
							onClose={onCloseDescriptionModal}
						/>
					</Modal>
				)}
				<Modal
					isOpen={isDeleteModalOpen}
					onClose={closeDeleteModal}
				>
					<RequestActionConfirmationModal
						acceptButtonTitle='Yes'
						cancelButtonTitle='No'
						closeModal={closeDeleteModal}
						description='Are you sure you want to delete selected rule(s)?'
						isWithoutComments={true}
						onApprove={handleDeleteNotification}
					/>
				</Modal>
				{editableAlertId && (
					<Modal
						height={MODAL_STYLES.HEIGHT}
						isConfirmDialogNeededOnClose
						isDirtyForm={isDirtyForm}
						isOpen={!!editableAlertId}
						isScrollableContent
						maxHeight={MODAL_STYLES.MAX_HEIGHT}
						onClose={handleCloseEditModal}
						title='Edit rule'
						width={MODAL_STYLES.WIDTH}
					>
						<EditKpiAlert
							closeModal={handleCloseEditModal}
							closeModalWithoutConfirm={closeEditForm}
							id={editableAlertId}
							setIsDirtyForm={setIsDirtyForm}
							vendor={selectedVendor}
						/>
					</Modal>
				)}
				<Modal
					isOpen={isCloseEditConfirmModalShown}
					onClose={closeEditConfirmModal}
				>
					<Confirm
						{...modalCloseConfirm}
						onClose={closeEditConfirmModal}
						onSubmit={closeEditForm}
					/>
				</Modal>
				<Modal
					isOpen={isExportModalOpen}
					onClose={closeExportModal}
					title='File extension to export'
				>
					<FileTypeSelectionCommonForm
						closeModals={closeExportModal}
						exportRadioButtonsData={exportRadioButtonsData}
						handlePreparingExport={handleExportKpiAlerts}
						isLoading={isExporting}
						toggleModal={closeExportModal}
					/>
				</Modal>
			</div>
		</>
	);
}

export default KpiAlertRulesTable;
